// Session store — текущий пользователь, тариф, read-only состояние и баны.
//
// Initial state — НЕ аутентифицирован. Реальную сессию подтягивает
// `AuthProvider` через tRPC `auth.me` (на dev сервер с DEV_AUTO_LOGIN
// возвращает фиксированного dev-юзера). Этот store — legacy-совместимость
// для кода, читающего user/tier синхронно; источник истины — AuthProvider.
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { persistStorage } from "./_storage";

export type SubscriptionTier = "free" | "plus" | "pro";

export interface SessionUser {
	id: string;
	name: string;
	email: string;
	/**
	 * Картинка, которую надо показать. Приходит из `auth.me` уже РАЗРЕШЁННОЙ:
	 * локальное фото поверх картинки единого профиля. Приоритет считает сервер
	 * (`shared/utils/resolve-avatar-url.ts`), клиент его не пересчитывает.
	 */
	avatarUrl?: string;
	/**
	 * Локальное фото загружено.
	 *
	 * Нужен там, где рядом есть свежепрочитанная сводка профиля: та знает только
	 * картинку ЭКОСИСТЕМЫ и, будучи предпочтена вслепую, вытеснила бы только что
	 * загруженное человеком фото.
	 */
	hasLocalAvatar?: boolean;
	/** Opaque DiceBear seed с сервера; персистим для офлайн-паритета. */
	avatarSeed?: string;
}

interface SessionState {
	user: SessionUser | null;
	tier: SubscriptionTier;
	readOnlyUntil: string | null;
	banned: boolean;
	isAuthenticated: boolean;
	setUser: (user: SessionUser | null) => void;
	setTier: (tier: SubscriptionTier) => void;
	logout: () => void;
}

export const useSessionStore = create<SessionState>()(
	persist(
		(set) => ({
			user: null,
			tier: "free",
			readOnlyUntil: null,
			banned: false,
			isAuthenticated: false,
			setUser: (user) => set({ user, isAuthenticated: user !== null }),
			setTier: (tier) => set({ tier }),
			logout: () => set({ user: null, tier: "free", isAuthenticated: false }),
		}),
		{
			name: "studyhub:session",
			storage: persistStorage,
			// Персистим только id и tier для оптимистичной hydration.
			// Реальные данные пользователя подтянет tRPC `session.me`.
			partialize: (state) => ({
				user: state.user
					? {
							id: state.user.id,
							name: state.user.name,
							email: state.user.email,
							// Нужен для DiceBear offline, пока auth.me не ответил.
							...(state.user.avatarSeed ? { avatarSeed: state.user.avatarSeed } : {}),
						}
					: null,
				tier: state.tier,
			}),
			// Optimistic hydration: если есть персистнутый user — считаем
			// аутентифицированным сразу, не дожидаясь сетевого auth.me. Иначе
			// офлайн cold-start показал бы GuestLanding (isAuthenticated не
			// персистится и при rehydrate был бы false). AuthProvider всё равно
			// перепроверит/уточнит при наличии сети.
			onRehydrateStorage: () => (state) => {
				if (state?.user) state.isAuthenticated = true;
			},
		},
	),
);
